import { Show, createMemo, createSignal } from "solid-js";

import LazyImg from "@/components/Base/LazyImg";
import Icon, { type svgPathTypes } from "@/components/Icon";
import { BetByGameId, FirstSportGameId } from "@/enum/game";
import { setFirstSportDetail } from "@/hooks/game/useFirstSport";
import { useRouter } from "@/hooks/useRouter";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { sysConfigStore } from "@/stores/sysConfigStore";
import { SportBetBySvgMap, SportFirstSvgMap } from "@/types/config";
import { SportEventInfo } from "@/types/swagger/extGame";

import { SPORTS_ICON_MAP } from "./constants";

type SportItemProps = {
  item: SportEventInfo;
};

// 体育详细信息

export const SearchSportItem = (props: SportItemProps) => {
  const item = props.item;
  const { push, preCheckEnterGame } = useRouter();

  const sportSvgMap = createMemo(() => {
    return sysConfigStore.sport_config.sport_priority_type === 1
      ? SportBetBySvgMap
      : SportFirstSvgMap;
  });

  const handleItemClick = async (item: SportEventInfo) => {
    const pass = await preCheckEnterGame({
      gid: `${item.FromType === 1 ? BetByGameId : item.FromType === 2 ? FirstSportGameId : ""}`,
      // itemId: `${props?.itemId}`,
      // bindPointType: bindPointType,
    });
    if (!pass) {
      return;
    }
    if (item.FromType === 1) {
      push(
        `/sports/${BetByGameId}/?bt-path=` + encodeURIComponent(item.BtPath),
      );
    } else if (item.FromType === 2) {
      push(`/sports/${FirstSportGameId}/`);
      setFirstSportDetail(item.BtPath);
    }
  };

  return (
    <div
      class="bg-general-bg3 flex w-full cursor-pointer flex-col items-center gap-[12px] rounded-[12px] py-[10px]"
      classList={{
        "px-[15px] h-[200px]": isMobile(),
        "px-[12px] h-[201px]": !isMobile(),
      }}
      onClick={() => handleItemClick(item)}
    >
      <div
        class="text-general-light flex w-full items-center text-left font-[600]"
        classList={{
          "text-[12px] leading-[15px]": isMobile(),
          "text-[14px] leading-[17px]": !isMobile(),
        }}
      >
        {/* 类别 - 赛组 */}
        <div class="flex flex-1 items-center font-[600]">
          <span class="max-w-[120px] truncate">{item.SportName}</span>
          <div class="bg-general-light mx-[6px] h-[4px] w-[4px] shrink-0 rounded-full"></div>
          <span class="max-w-[180px] truncate">{item.LeagueName}</span>
        </div>
        {/* 是否直播 */}
        <Show when={item.IsLive} fallback={<></>}>
          <div class="bg-home-main10 text-general-main flex h-[24px] min-w-[48px] shrink-0 items-center justify-center gap-x-[2px] whitespace-nowrap rounded-[6px] px-[5px] font-normal">
            <Icon name="general/play" class="text-general-main size-[12px]" />
            <span class="text-[12px] font-[400] leading-[15px]">
              {t("common.live")}
            </span>
          </div>
        </Show>
      </div>
      {/* 比赛信息 */}
      <div class="flex h-[200px] w-full flex-col items-center gap-[10px]">
        <div
          class="text-general-light flex max-h-fit w-full flex-1"
          classList={{
            "h-[96px]": isMobile(),
            "h-[98px]": !isMobile(),
          }}
        >
          <div
            class="flex flex-1 flex-col items-center justify-between gap-[4px]"
            classList={{
              "h-[96px]": isMobile(),
              "h-[98px]": !isMobile(),
            }}
          >
            <div
              class="bg-home-bg6 flex items-center justify-center gap-[4px] rounded-[9.68px] p-[10px]"
              classList={{
                "h-[62px] w-[62px]": isMobile(),
                "h-[60px] w-[60px]": !isMobile(),
              }}
            >
              <TeamIcon
                imageUrl={item.Participants?.[0]?.Url}
                role="home"
                fallbackIcon={`sports/new-icon/${sportSvgMap()[item.SportId as keyof typeof sportSvgMap]}`}
              />
            </div>
            <div
              class="flex h-[30px] w-full items-center justify-center overflow-hidden text-center font-[600]"
              classList={{
                "text-[12px] leading-[15px]": isMobile(),
                "text-[14px] leading-[17px]": !isMobile(),
              }}
            >
              <div class="line-clamp-2 w-full overflow-hidden break-words text-center">
                {item.Participants?.[0]?.Name || "-"}
              </div>
            </div>
          </div>
          {/* 比分 */}
          <div class="flex flex-1 flex-col items-center justify-center gap-[4px]">
            <div class="flex items-center justify-center gap-[5px] text-[28px] font-[800] leading-[32px]">
              <span>{item.HomeScore}</span>
              <span>:</span>
              <span>{item.AwayScore}</span>
            </div>
            <div
              class="text-general-bright font-Inter font-[400]"
              classList={{
                "text-[14px] leading-[17px]": isMobile(),
                "text-[15px] leading-[18px]": !isMobile(),
              }}
            >
              {item.GamePart}
            </div>
          </div>
          {/* 客队 Icon 名称 */}
          <div class="flex h-[96px] flex-1 flex-col items-center justify-between">
            <div
              class="bg-home-bg6 flex items-center justify-center gap-[4px] rounded-[9.68px] p-[10px]"
              classList={{
                "h-[62px] w-[62px]": isMobile(),
                "h-[60px] w-[60px]": !isMobile(),
              }}
            >
              <TeamIcon
                imageUrl={item.Participants?.[1]?.Url}
                role="home"
                fallbackIcon={`sports/new-icon/${sportSvgMap()[item.SportId as keyof typeof sportSvgMap]}`}
              />
            </div>
            <div
              class="flex h-[30px] max-w-[109px] items-center justify-center overflow-hidden text-center font-[600]"
              classList={{
                "text-[12px] leading-[15px]": isMobile(),
                "text-[14px] leading-[17px]": !isMobile(),
              }}
            >
              <div class="line-clamp-2 w-full overflow-hidden break-words text-center">
                {item.Participants?.[1]?.Name || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 市场信息 */}
      <div
        class="flex w-full cursor-pointer gap-[6px] font-[600] leading-[100%]"
        classList={{
          "text-[14px] h-[36px]": isMobile(),
          "text-[12px] h-[35px]": !isMobile(),
        }}
      >
        <Show when={item.Markets?.Selections?.length} fallback={<></>}>
          {/* 主场赔率 */}
          <Show
            when={item.Markets?.Selections?.[0]?.DisplayOdds}
            fallback={<></>}
          >
            <button
              class="text-general-light bg-home-tagdi flex flex-1 cursor-pointer items-center justify-center gap-x-[8px] rounded-[6px] text-[12px]"
              classList={{
                "text-[14px] h-[36px]": isMobile(),
                "text-[12px] h-[35px]": !isMobile(),
              }}
            >
              1
              <div class="text-general-tag font-[600]">
                {item.Markets?.Selections?.[0]?.DisplayOdds}
              </div>
            </button>
          </Show>
          {/* 平局赔率 */}
          <Show
            when={item.Markets?.Selections?.[1]?.DisplayOdds}
            fallback={<></>}
          >
            <button
              class="text-general-light bg-home-tagdi flex flex-1 cursor-pointer items-center justify-center gap-x-[8px] rounded-[6px] text-[12px]"
              classList={{
                "text-[14px] h-[36px]": isMobile(),
                "text-[12px] h-[35px]": !isMobile(),
              }}
            >
              {t("common.tie")}
              <div class="text-general-tag font-[600]">
                {item.Markets?.Selections?.[1]?.DisplayOdds}
              </div>
            </button>
          </Show>
          {/* 客场赔率 */}
          <Show
            when={item.Markets?.Selections?.[2]?.DisplayOdds}
            fallback={<></>}
          >
            <button
              class="text-general-light bg-home-tagdi flex flex-1 cursor-pointer items-center justify-center gap-x-[8px] rounded-[6px] text-[12px]"
              classList={{
                "text-[14px] h-[36px]": isMobile(),
                "text-[12px] h-[35px]": !isMobile(),
              }}
            >
              2
              <div class="text-general-tag font-[600]">
                {item.Markets?.Selections?.[2]?.DisplayOdds}
              </div>
            </button>
          </Show>
        </Show>
        {/* 市场总数 */}
        <button class="bg-home-bg7 text-general-light flex flex-1 cursor-pointer items-center justify-center gap-[3.92px] rounded-[6px] text-[12px] font-[600]">
          +<span>{item.TotalActiveMarketsCount || 1}</span>
        </button>
      </div>
    </div>
  );
};

type TeamIconProps = {
  role: "home" | "away";
  imageUrl: string;
  fallbackIcon: svgPathTypes;
};

// 主队图标组件
const TeamIcon = (props: TeamIconProps) => {
  const [imageError, setImageError] = createSignal(false);

  return (
    <Show
      when={props.imageUrl && !imageError()}
      fallback={
        <Icon
          name={props.fallbackIcon}
          class={`text-general-icon flex ${isMobile() ? "h-[42px] w-[42px]" : "h-[40px] w-[40px]"}`}
        />
      }
    >
      <LazyImg
        src={props.imageUrl}
        alt="team icon"
        class={`object-contain ${isMobile() ? "h-[42px] w-[42px]" : "h-[40px] w-[40px]"}`}
        onError={() => setImageError(true)}
      />
    </Show>
  );
};
